/** Print, as five documents: the paper for the student, the mark scheme on its own for
 * whoever marks it, the paper with the scheme under each question, lined answer sheets, and
 * the paper and its scheme in one job. Every one carries the paper's tag (PrintTag). */
import { useEffect, useRef, useState } from "react";

export type PrintMode = "paper" | "scheme" | "answers" | "sheets" | "both";

const CHOICES: { mode: PrintMode; label: string }[] = [
  { mode: "paper", label: "Question paper" },
  { mode: "scheme", label: "Mark scheme" },
  { mode: "answers", label: "Paper with answers" },
  { mode: "sheets", label: "Answer sheets" },
  { mode: "both", label: "Paper and mark scheme" },
];

export function PrintMenu({ onPrint }: { onPrint: (mode: PrintMode) => void }) {
  const [open, setOpen] = useState(false);
  const root = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const onDown = (e: MouseEvent) => {
      if (root.current && !root.current.contains(e.target as Node)) setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => e.key === "Escape" && setOpen(false);
    document.addEventListener("mousedown", onDown);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDown);
      document.removeEventListener("keydown", onKey);
    };
  }, [open]);

  return (
    <div className="print-menu" ref={root}>
      <button
        type="button"
        className="button"
        aria-haspopup="true"
        aria-expanded={open}
        onClick={() => setOpen((o) => !o)}
      >
        Print
      </button>
      {open && (
        <div className="print-menu__panel" role="menu" aria-label="Print">
          {CHOICES.map(({ mode, label }) => (
            <button
              key={mode}
              type="button"
              role="menuitem"
              onClick={() => {
                setOpen(false);
                onPrint(mode);
              }}
            >
              {label}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
